import { fetchOrder, getOrderImages, fetchOrderGenerateCampaignStatus } from '@/api/order';
import { fetchCompany, fetchCompanyImages } from '@/api/company';
import { fetchProducts } from '@/api/product';
import {
  createCustomerPublication,
  createOrderDraft,
  getCustomerPublication,
  getPublicationTemplates,
  updateCustomerPublication,
  updateOrderDraft,
} from '@/api/publication';
import { updateMetaDocument } from '@/api/meta';
import { generateSelectedProductsArray } from '@/components/orders/product/functions';
import { formatDateTime, PJAS_DATE_FORMAT } from '@/filters/dateFormat';
import type { ScanTimelineItem } from '@/components/orders/wizard_sections/WizardScanTimeline.vue';
import type { IProduct, IProductToSave } from '@/types/product';
import type { PublicationTemplate, IPublicationData, PublicationType } from '@/types/publication';
import type { Order, OrderMedia, ImageObject } from '@/types/types';
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { rememberPlacedOrder } from '@/func/placedOrders';

export interface WizardSection {
  visible: boolean;
  canGoNext: boolean;
  canGoBack?: boolean;
  shouldBeAwaited?: boolean; // if true, the loading spinner in the CreateOrderPageNew will await for this step to have the isReady = true
  onContinue?: () => void;
}

export type WizardStepKey = (typeof availableStepKeys)[number];

type WizardStepConfig = Record<WizardStepKey, WizardSection>;

type StepVisibilityContext = {
  skipUrl: boolean;
  skipImport: boolean;
  skipDetails: boolean;
  skipMedia: boolean;
  isDirectLink: boolean;
  selfServiceEnabled: boolean;
  orderPlaced: boolean;
};

type ResolvedWizardStep = {
  requestedStep: string | undefined;
  step: { key: string; sequence: number; section: WizardSection } | undefined;
  fallbackStep: { key: string; sequence: number; section: WizardSection } | undefined;
  isKnown: boolean;
  isVisible: boolean;
  canRestore: boolean;
};

//@ts-expect-error - Default order state
const defaultOrderState: Order = {
  workload_fulltime: true,
  workload_min: 100,
  workload_max: 100,
  workload_permanent: true,
  application_receivers: [],
  rte_blocks: [],
  external_confirmation: false,
  initial_job_posting: false,
} as Order;

export const availableStepKeys: string[] = [
  'source',
  'import',
  'details',
  'media',
  'publication_type',
  'publication_template',
  'publication_editor',
  'products',
  'self_service',
  'final_review',
  'confirmation',
];

/**
 * Boron pipeline task statuses (Boron\Pipeline\PipelineTask::statusString) that mean the
 * `job.generate_campaign` task still has work ahead of it. A task whose dependencies (banner
 * and video banner generation) haven't finished stays on `waiting`, and `blocked` is handed
 * out whenever the task is scheduled for a later execution date.
 */
const CAMPAIGN_TASK_PENDING_STATUSES = [
  'waiting',
  'in_progress',
  'blocked',
  'blocked_by_subflow',
  'postponed',
  'postpone_scheduled',
];

// heavy orders (many banners and video banners) can take several minutes to generate
const CAMPAIGN_POLL_INTERVAL_MS = 3000;
const CAMPAIGN_POLL_MAX_ATTEMPTS = 200; // ≈10 minutes at the default interval

export type CampaignTaskState = 'none' | 'pending' | 'success' | 'failed';

/**
 * Which stage of the pipeline the generation loader is showing.
 *
 * `job.generate_campaign` depends on `job.generate_banners`, so it only leaves `waiting` once the
 * banners (and video banners) have finished rendering. Everything before the campaign task starts
 * is therefore still the banner stage — inferred from the campaign task alone, since the status
 * endpoint does not report the banner task.
 */
export type CampaignGenerationPhase = 'banners' | 'campaigns';

/**
 * Map a `job.generate_campaign` pipeline task onto the four states the wizard cares about.
 *
 * `none` means no task row exists, which the backend also reports while the task hasn't been
 * queued yet — it is not a "finished" signal. Note the backend never reports `failed`: an
 * unsuccessful task is `error`, and it keeps its place in the queue for as long as it has
 * retry budget left (mirrors PipelineManager::isRetryExhausted).
 */
export function resolveCampaignTaskState(task: Record<string, any> | null | undefined): CampaignTaskState {
  const status = task?.status_string;
  if (!task || !status) return 'none';
  if (status === 'success') return 'success';
  if (CAMPAIGN_TASK_PENDING_STATUSES.includes(status)) return 'pending';

  if (status === 'error') {
    const allowedAttempts = Number(task.allowed_attempts ?? 0);
    const attempts = Number(task.attempts ?? 0);
    return allowedAttempts > 1 && attempts < allowedAttempts ? 'pending' : 'failed';
  }

  // timed_out, exec_canceled and anything unknown are terminal
  return 'failed';
}

const stepVisibilityRules: Record<WizardStepKey, (context: StepVisibilityContext) => boolean> = {
  source: ({ skipUrl }) => !skipUrl,
  import: ({ skipImport }) => !skipImport,
  details: ({ skipDetails }) => !skipDetails,
  media: ({ skipMedia }) => !skipMedia,
  publication_type: ({ isDirectLink }) => !isDirectLink,
  publication_template: ({ isDirectLink }) => !isDirectLink,
  publication_editor: ({ isDirectLink }) => !isDirectLink,
  products: () => true,
  self_service: ({ selfServiceEnabled }) => selfServiceEnabled,
  final_review: () => true,
  // terminal step, only reachable by placing the order, never by restoring a draft
  confirmation: ({ orderPlaced }) => orderPlaced,
};

export const useOrderCreationStore = defineStore('orderCreation', () => {
  const router = useRouter();
  const customerFeaturesStore = useCustomerFeaturesStore();

  const order = ref<Order>({ ...defaultOrderState });

  const publication = ref<PublicationType>();
  const publicationTemplates = ref<PublicationTemplate[]>([]);

  const isLoading = ref(false);
  const shouldCreatePublication = ref(!router.currentRoute.value.query.pubkey);
  const isPublicationLoading = ref(false);
  const publicationSavingState = ref<'idle' | 'typing' | 'saving' | 'saved' | 'error'>('idle');
  const isTemplateLoading = ref(false);

  // Campaign generation polling
  const isCampaignGenerationLoading = ref(false);
  const campaignGenerationTask = ref<Record<string, any> | null>(null);
  const generatedCampaigns = ref<any[]>([]);
  let campaignPollTimer: ReturnType<typeof setTimeout> | null = null;

  const orderProducts = ref<IProduct[]>([]);
  const productsToSave = ref<IProductToSave[]>([]);
  const orderImages = ref<OrderMedia>({ companyLogo: [], images: [] });
  const companyImages = ref<ImageObject[]>([]);
  const setDefaultCompanyLogo = ref<boolean>(false);

  const customContentGeneration = ref<boolean>(false);

  const isFunnel = ref(false);
  const stepErrors = ref<Record<string, string[]>>({});
  const generateQuestions = ref(true);
  const optimizeContent = ref(true);

  /* ----------------------------- WIZARD STEPS ------------------------------ */
  const currentStepSequence = ref(1);
  const openCart = ref(false);
  const skipUrlStep = ref(false);
  const skipImportStep = ref(false);
  const skipDetailsStep = ref(false);
  const skipMediaStep = ref(false);
  const isOrderPlaced = ref(false);
  const isSelfServiceEnabled = computed(() => customerFeaturesStore.featureOrderManagment?.self_service === true);

  function getDefaultStepConfig(): WizardStepConfig {
    return {
      source: { visible: true, canGoNext: false },
      import: { visible: true, canGoNext: false },
      details: { visible: true, canGoNext: false, shouldBeAwaited: true },
      media: { visible: true, canGoNext: true },
      publication_type: { visible: true, canGoNext: false },
      publication_template: { visible: true, canGoNext: false },
      publication_editor: { visible: true, canGoNext: false },
      products: {
        visible: true,
        canGoNext: false,
        onContinue: () => {
          openCart.value = true;
          return true;
        },
      },
      self_service: { visible: false, canGoNext: false, canGoBack: false },
      final_review: { visible: true, canGoNext: false, canGoBack: false },
      confirmation: { visible: false, canGoNext: false, canGoBack: false },
    };
  }

  const availableSteps = ref<WizardStepConfig>(getDefaultStepConfig());

  function buildStepVisibilityContext(overrides: Partial<StepVisibilityContext> = {}): StepVisibilityContext {
    return {
      skipUrl: overrides.skipUrl ?? skipUrlStep.value,
      skipImport: overrides.skipImport ?? skipImportStep.value,
      skipDetails: overrides.skipDetails ?? skipDetailsStep.value,
      skipMedia: overrides.skipMedia ?? skipMediaStep.value,
      isDirectLink: overrides.isDirectLink ?? order.value.creation_source === 'direct_url',
      selfServiceEnabled: overrides.selfServiceEnabled ?? isSelfServiceEnabled.value,
      orderPlaced: overrides.orderPlaced ?? isOrderPlaced.value,
    };
  }

  function syncStepVisibility(overrides: Partial<StepVisibilityContext> = {}) {
    const context = buildStepVisibilityContext(overrides);

    skipUrlStep.value = context.skipUrl;
    skipImportStep.value = context.skipImport;
    skipDetailsStep.value = context.skipDetails;
    skipMediaStep.value = context.skipMedia;

    for (const key of availableStepKeys as WizardStepKey[]) {
      availableSteps.value[key].visible = stepVisibilityRules[key](context);
    }

    return context;
  }

  /* -------------------------- ANALYTICS PARAMETERS -------------------------- */
  const previewImageLoaded = ref(false);
  const previewImageUrl = ref<string | undefined>();
  const didScanJobPosting = ref(false);
  const scanningInProgress = ref(false);
  /**
   * Scanning Timeline
   */
  // the timeline is mostly fake and serves only the purpose of
  // distracting the user while we run our AI bot over the job posting
  // this takes up to 20 seconds..
  const scanTimeline = shallowRef<ScanTimelineItem[]>([]);
  const scanTimelineIsDone = shallowRef(false);
  const statusMessages = ref<ScanTimelineItem[]>([]);
  const statusMessagesIsDone = ref(false);

  /* ---------------------------- SETTERS / METHODS --------------------------- */
  function resetState() {
    order.value = { ...defaultOrderState };
    publication.value = undefined;
    publicationTemplates.value = [];
    shouldCreatePublication.value = !router.currentRoute.value.query.pubkey;
    isPublicationLoading.value = false;
    publicationSavingState.value = 'idle';
    isTemplateLoading.value = false;

    // Reset campaign generation
    isCampaignGenerationLoading.value = false;
    campaignGenerationTask.value = null;
    generatedCampaigns.value = [];
    stopCampaignGenerationPolling();

    // Reset products
    orderProducts.value = [];
    productsToSave.value = [];
    orderImages.value = { companyLogo: [], images: [] };
    companyImages.value = [];
    setDefaultCompanyLogo.value = false;

    // Reset content generation options
    customContentGeneration.value = false;
    isFunnel.value = false;
    generateQuestions.value = true;
    optimizeContent.value = true;

    // Analytics
    previewImageLoaded.value = false;
    previewImageUrl.value = undefined;
    didScanJobPosting.value = false;
    scanningInProgress.value = false;
    scanTimeline.value = [];
    scanTimelineIsDone.value = false;
    statusMessages.value = [];
    statusMessagesIsDone.value = false;

    // Reset wizard state
    currentStepSequence.value = 1;
    const defaults = getDefaultStepConfig();
    Object.keys(defaults).forEach((key) => {
      const k = key as keyof typeof availableSteps.value;
      availableSteps.value[k] = defaults[k];
    });
    openCart.value = false;
    skipUrlStep.value = false;
    skipImportStep.value = false;
    skipDetailsStep.value = false;
    skipMediaStep.value = false;
    isOrderPlaced.value = false;
    syncStepVisibility();
  }

  // the order was submitted successfully, reveal the confirmation step and jump to it
  function markOrderPlaced() {
    isOrderPlaced.value = true;
    // placing finalizes the order, keep local state in line with the backend
    order.value.is_draft = false;
    if (order.value.id) rememberPlacedOrder(order.value.id);
    syncStepVisibility();
    const confirmationStep = visibleSteps.value.find((step) => step.key === 'confirmation');
    if (confirmationStep) currentStepSequence.value = confirmationStep.sequence;
  }

  // Configure wizard steps without changing the current step sequence (for existing orders)
  function configureWizardSteps(
    skipUrl = false,
    skipImport = false,
    selfServiceEnabled = isSelfServiceEnabled.value,
    skipDetails = false,
    skipMedia = false
  ) {
    syncStepVisibility({ skipUrl, skipImport, selfServiceEnabled, skipDetails, skipMedia });
  }
  // Configure publication steps based on skip flags
  function configurePublicationSteps(isDirectLink: boolean, selfServiceEnabled = isSelfServiceEnabled.value) {
    syncStepVisibility({ isDirectLink, selfServiceEnabled });
  }

  function resolveRequestedStep(stepQuery: string | null | undefined): ResolvedWizardStep {
    const requestedStep = stepQuery?.split(':')[0];
    if (!requestedStep) {
      return {
        requestedStep,
        step: undefined,
        fallbackStep: undefined,
        isKnown: false,
        isVisible: false,
        canRestore: false,
      };
    }

    if (!(availableStepKeys as string[]).includes(requestedStep)) {
      return {
        requestedStep,
        step: undefined,
        fallbackStep: undefined,
        isKnown: false,
        isVisible: false,
        canRestore: false,
      };
    }

    const requestedStepKey = requestedStep as WizardStepKey;

    const step = visibleSteps.value.find((visibleStep) => visibleStep.key === requestedStepKey);
    const requestedIndex = (availableStepKeys as WizardStepKey[]).indexOf(requestedStepKey);
    let fallbackKey: WizardStepKey | undefined;

    // If the requested step is no longer visible after a refresh, keep the user
    // close to where they were by preferring the nearest next visible step.
    for (let index = requestedIndex + 1; index < availableStepKeys.length; index++) {
      const candidateKey = availableStepKeys[index] as WizardStepKey;
      if (availableSteps.value[candidateKey]?.visible) {
        fallbackKey = candidateKey;
        break;
      }
    }

    // Only fall back when there is no later visible step left.
    if (!fallbackKey) {
      for (let index = requestedIndex - 1; index >= 0; index--) {
        const candidateKey = availableStepKeys[index] as WizardStepKey;
        if (availableSteps.value[candidateKey]?.visible) {
          fallbackKey = candidateKey;
          break;
        }
      }
    }

    const fallbackStep = fallbackKey
      ? visibleSteps.value.find((visibleStep) => visibleStep.key === fallbackKey)
      : undefined;
    const canRestore = !!step && step.sequence >= orderDetailsStep.value.sequence;

    return {
      requestedStep,
      step,
      fallbackStep,
      isKnown: true,
      isVisible: !!step,
      canRestore,
    };
  }

  function restoreStepFromQuery(stepQuery: string | null | undefined) {
    const result = resolveRequestedStep(stepQuery);
    if (result.canRestore && result.step) {
      currentStepSequence.value = result.step.sequence;
    }
    return result;
  }

  // Called when products are validated in the cart drawer
  function onProductsValidated() {
    openCart.value = false;
    order.value.step = 'products';
    order.value.selected_products = generateSelectedProductsArray(productsToSave.value);

    const isSelfServiceEnabled = customerFeaturesStore.featureOrderManagment?.self_service === true;
    const canFinalizeOrder = customerFeaturesStore.featureOrderManagment?.auto_finalize_orders === true;

    const params = canFinalizeOrder ? { finalize: 1 } : {};

    updateDraft(params).then(() => {
      if (isSelfServiceEnabled && order.value.id) {
        pollCampaignGenerationStatus(order.value.id, {
          onComplete: () => {
            currentStepSequence.value++;
          },
          onError: (err) => {
            console.error('Campaign generation failed:', err);
          },
        });
      } else {
        currentStepSequence.value++;
      }
    });
  }

  // tree_type isn't honored by the order endpoint's company resolver, and the LEAD
  // draft endpoint drops query params in OrderRequest::toPJASPayload() so the draft
  // response comes back without company.logo_image. We follow up with a direct
  // fetchCompany that resolves both the full ancestor chain and all logos, and use
  // it as the source of truth for order.company.data.
  async function hydrateCompanyHierarchy() {
    const companyId = order.value.company_id;
    if (!companyId) return;
    try {
      const res = await fetchCompany(companyId, { resolve: 'logo_image', tree_type: 'ancestors' });
      const companyData = res?.data;
      if (!companyData) return;
      // CompanyTransformer returns `parent: []` when there is no parent
      const rawParent = companyData.parent;
      const normalizedParent =
        rawParent && typeof rawParent === 'object' && !Array.isArray(rawParent) ? rawParent : null;
      order.value.company = {
        data: {
          ...companyData,
          parent: normalizedParent,
        } as any,
      };
    } catch (error) {
      console.error('Error hydrating company hierarchy:', error);
    }
  }

  async function getOrderById(orderId: string | number | null | undefined) {
    if (orderId) {
      try {
        const res = await fetchOrder(orderId, {
          resolve: 'company,company.logo_image,job_products,application_receivers',
        });
        if (!res?.data?.id || !res?.data?.title) throw new Error('Order data is incomplete');

        order.value = {
          ...res?.data,
          external_confirmation: res?.data?.external_confirmation ?? false,
        };
        await hydrateCompanyHierarchy();
        syncStepVisibility();
        const orderMedia = await getOrderImages(orderId);

        if (orderMedia.images) {
          orderMedia.images.forEach((img) => (img.selected = true));
        }
        if (orderMedia.companyLogo) {
          orderMedia.companyLogo.forEach((img) => (img.selected = true));
        }

        orderImages.value = orderMedia;
      } catch (error) {
        throw error;
      }
    }
  }

  async function getOrderProducts() {
    await fetchProducts({ resolve: 'image,product_options' }).then((res) => {
      orderProducts.value = res.data.filter((product: IProduct) => product.enabled);

      if (order.value.job_products?.data?.length) {
        order.value.job_products.data.forEach((selectedProduct) => {
          const matchingProduct = orderProducts.value.find((p) => p.id === selectedProduct.product_id);
          if (matchingProduct) {
            const currency = selectedProduct.price_currency || selectedProduct.currency_final || 'CHF';
            const vBudget = selectedProduct.vbudget_id
              ? {
                  id: selectedProduct.vbudget_id,
                  currency: currency,
                }
              : undefined;
            productsToSave.value.push({
              id: matchingProduct.id,
              name: matchingProduct.name,
              image: matchingProduct.image?.data?.proxy.url_preview,
              startdate: selectedProduct.start_final && formatDateTime(selectedProduct.start_final, PJAS_DATE_FORMAT),
              enddate: selectedProduct.end_final && formatDateTime(selectedProduct.end_final, PJAS_DATE_FORMAT),
              budget: selectedProduct.budget_final,
              metric_goal: selectedProduct.metric_goal,
              spending_limit: selectedProduct.spending_limit,
              selectedOptions: selectedProduct.product_options?.reduce((acc, option) => {
                acc[option.id] = option.value;
                return acc;
              }, {}),
              currency: currency,
              vBudget: vBudget,
            } as any);
          }
        });
      }
    });
  }

  // Fire-and-forget: once the web-import blocks finish loading, write them to the order's meta
  // document so they survive a mid-wizard page reload. Non-blocking so the wizard can advance.
  function persistImportedBlocksToOrder(orderId: number | string) {
    import('./importOrderCreation')
      .then(async ({ useImportOrderCreationStore }) => {
        const importOrderCreationStore = useImportOrderCreationStore();
        if (importOrderCreationStore.jobImportBlocksPromise) {
          await importOrderCreationStore.jobImportBlocksPromise;
        }
        if (importOrderCreationStore.jobImportBlocks?.length) {
          await updateMetaDocument(orderId, importOrderCreationStore.jobImportBlocks);
        }
      })
      .catch((e) => console.error('Failed to persist imported blocks to order meta document:', e));
  }

  async function generateDraft() {
    if (order.value.id) return;

    try {
      // skipDefaultErrorHandler: the generic "Request failed: Validation failed." toast is
      // useless here (e.g. when the customer is bound_by_budget but has no active budget).
      // Callers handle draft-creation failures inline instead.
      const draftOrder = await createOrderDraft(
        order.value,
        { resolve: 'company,company.logo_image' },
        { skipDefaultErrorHandler: true }
      );
      const draftOrderId = draftOrder.data.id;

      if (!draftOrderId) {
        throw new Error('Failed to create order draft or retrieve its ID.');
      }

      order.value = { ...order.value, ...draftOrder.data };
      await hydrateCompanyHierarchy();

      // Persist the web-import blocks to the order's meta document as soon as the draft exists.
      // This protects against page reloads mid-wizard: if the user resumes the draft, the blocks
      // are already saved server-side. fetchJobPosting later reads this same order-scoped document
      // directly - there is no funnel-only mirroring step, it's the single source of truth for both
      // publication paths.
      persistImportedBlocksToOrder(draftOrderId);

      return order.value;
    } catch (error) {
      console.error('Error saving ad data:', error);
      throw error;
    }
  }

  async function updateDraft(params: Record<string, any> = {}) {
    try {
      if (!order.value.id) throw new Error('Order draft ID is missing. Cannot update order draft.');

      // Prepare draft data from order details
      if (!params.resolve) params.resolve = 'company,application_receivers,job_products,company.logo_image';
      const updatedOrder = await updateOrderDraft(order.value.id, order.value, params);

      const { company: _incomingCompany, ...restUpdated } = updatedOrder.data as any; // skip company in the merge and let hydrateCompanyHierarchy handle it separately (to avoid flashing on screen)
      order.value = {
        ...order.value,
        ...restUpdated,
        directlink: order.value.directlink || restUpdated.directlink,
        external_confirmation: updatedOrder.data?.external_confirmation ?? false,
      };
      stepErrors.value = {};
      await hydrateCompanyHierarchy();
    } catch (error) {
      console.error('Error updating order draft:', error);
      throw error;
    }
  }

  async function getPublicationByKey(pubkey: string) {
    if (pubkey) {
      isPublicationLoading.value = true;
      try {
        const res = await getCustomerPublication(pubkey);
        publication.value = res?.data || {};
        isPublicationLoading.value = false;
        isFunnel.value = publication.value?.funnel_enabled || false;
        generateQuestions.value = publication.value?.generate_questions ?? false;
        optimizeContent.value = publication.value?.optimize_content ?? false;

        if (order.value.creation_source !== 'direct_url' && publication.value?.public_url) {
          order.value.directlink = publication.value.public_url;
        }

        return res?.data;
      } catch (error) {
        throw error;
      }
    }
  }

  // lazily create the publication for an explicitly chosen template (+ optional variation).
  // throws on failure so the caller can surface it; the loader is always cleared via finally.
  async function createPublication(orderId: string | number, templateKey: string, variationKey: string | null = null) {
    if (!orderId) throw new Error('Something went wrong while generating the publication');
    if (!templateKey) throw new Error('A template is required to create a publication');

    isPublicationLoading.value = true;
    try {
      const res = await createCustomerPublication({
        template: templateKey,
        variation: variationKey ?? undefined,
        name: order.value.title!,
        order_id: +orderId,
        isFunnel: isFunnel.value,
        optimizeContent: optimizeContent.value,
        generateQuestions: generateQuestions.value,
      });
      if (!res.data?.pubkey) throw new Error('Publication creation was incomplete');

      publication.value = res.data;
      shouldCreatePublication.value = false;
      router.replace({ query: { ...router.currentRoute.value.query, pubkey: res.data.pubkey } });

      if (order.value.creation_source !== 'direct_url' && publication.value?.public_url) {
        order.value.directlink = publication.value.public_url;
        // try to update, but don't block the flow if it fails
        updateDraft().catch((error) => {
          console.error('Failed to update draft with publication URL:', error);
        });
      }

      return res.data;
    } finally {
      isPublicationLoading.value = false;
    }
  }
  async function updatePublication(pubKey: string, body: Partial<IPublicationData>) {
    if (!pubKey) throw new Error('Publication key is missing');

    const res = await updateCustomerPublication(pubKey, body);
    if (!res.data.pubkey) throw new Error('Publication update was incomplete');

    publication.value = res.data;
  }
  async function fetchPublicationTemplates() {
    try {
      // with a pubkey the publication's current template stays listed even if it became exclusive elsewhere
      const pubKey = publication.value?.pubkey;
      const res = await getPublicationTemplates<PublicationTemplate[]>(pubKey ? { pubKey } : undefined);
      if (!res.data) throw new Error('Failed to retrieve publication templates');

      publicationTemplates.value = res.data;
    } catch (error) {
      console.error('Error retrieving publication templates:', error);
    }
  }

  /* -------------------------------- WATCHERS -------------------------------- */
  watch(
    publication,
    () => {
      if (publication.value) {
        isFunnel.value = publication.value?.funnel_enabled || false;
      }
    },
    { immediate: true, deep: true }
  );

  watch(
    () => order.value.creation_source,
    (newImportType) => {
      if (newImportType) configurePublicationSteps(newImportType === 'direct_url');
    },
    { immediate: true }
  );

  watch(
    isSelfServiceEnabled,
    (selfServiceEnabled) => {
      syncStepVisibility({ selfServiceEnabled });
    },
    { immediate: true }
  );

  async function fetchCompanyImagesForCurrentOrder() {
    if (order.value.company_id) {
      try {
        const res = await fetchCompanyImages(order.value.company_id, { type: 'image' });
        if (Array.isArray(res.data)) {
          companyImages.value = res.data;
        } else {
          companyImages.value = [];
        }
      } catch (error) {
        console.error('Failed to fetch company images:', error);
        companyImages.value = [];
      }
    } else {
      companyImages.value = [];
    }
  }

  watch(
    () => order.value.company_id,
    () => {
      fetchCompanyImagesForCurrentOrder();
    },
    { immediate: true }
  );

  /* -------------------------------- GETTERS -------------------------------- */

  const orderDetailsStep = computed(() => {
    const detailsStep = visibleSteps.value.find((step) => step.key === 'details');
    return detailsStep || { key: 'source', sequence: 1, section: availableSteps.value.source };
  });

  const companylogo = computed(() => {
    const logos = orderImages.value.companyLogo;
    return logos.length ? logos[0] : null;
  });

  // Follow productsToSave's order (pick-order), not the catalog order from orderProducts,
  // so downstream things like expectedCampaignProviders reflect how the user picked them.
  const selectedProducts = computed(() => {
    if (!orderProducts.value.length) return [];
    const byId = new Map(orderProducts.value.map((op) => [op.id, op]));
    return productsToSave.value.map((p) => byId.get(p.id)).filter((op): op is IProduct => !!op);
  });

  // Unique campaign_providers across all selected products, preserving first-seen order
  const expectedCampaignProviders = computed<string[]>(() => {
    const seen: string[] = [];
    for (const product of selectedProducts.value) {
      for (const cp of (product.campaign_providers || []) as string[]) {
        if (!seen.includes(cp)) seen.push(cp);
      }
    }
    return seen;
  });

  const visibleSteps = computed(() => {
    return Object.entries(availableSteps.value)

      .filter(([_, step]) => step.visible)
      .map(([key, step], index) => ({ key, sequence: index + 1, section: step }));
  });

  const currentProgress = computed(() => {
    return (currentStepSequence.value / visibleSteps.value.length) * 100;
  });

  const currentStep = computed(() => {
    return visibleSteps.value[currentStepSequence.value - 1] || {};
  });

  const canClickNext = computed(() => {
    return currentStep.value?.section?.canGoNext || false;
  });

  /**
   * The stage the generation loader should present. Anything before the campaign task starts
   * running is still the banner stage, since that is what it is queued behind.
   */
  const campaignGenerationPhase = computed<CampaignGenerationPhase>(() => {
    const status = campaignGenerationTask.value?.status_string;
    return status === 'in_progress' || status === 'success' ? 'campaigns' : 'banners';
  });

  /**
   * Poll the campaign generation status endpoint until campaigns are ready
   * or the task is no longer running.
   */
  async function pollCampaignGenerationStatus(
    orderId: string | number,
    options?: {
      intervalMs?: number;
      maxAttempts?: number;
      startDelayMs?: number;
      onComplete?: (campaigns: any[]) => void;
      onError?: (error: any) => void;
    }
  ) {
    const {
      intervalMs = CAMPAIGN_POLL_INTERVAL_MS,
      maxAttempts = CAMPAIGN_POLL_MAX_ATTEMPTS,
      startDelayMs = 0,
      onComplete,
      onError,
    } = options || {};
    let attempts = 0;

    isCampaignGenerationLoading.value = true;
    stopCampaignGenerationPolling();

    const poll = async () => {
      try {
        attempts++;
        const res = await fetchOrderGenerateCampaignStatus(orderId);

        const task: Record<string, any> | null = res.task ?? null;
        const campaigns: any[] = res.data || [];

        campaignGenerationTask.value = task;
        generatedCampaigns.value = campaigns;

        const hasCampaigns = Array.isArray(campaigns) && campaigns.length > 0;
        const taskState = resolveCampaignTaskState(task);

        if (taskState === 'failed') {
          isCampaignGenerationLoading.value = false;
          onError?.(new Error(`Campaign generation task failed with status "${task?.status_string}"`));
          return;
        }

        if (taskState === 'success') {
          // brief pause so the user sees the final colored icon row before the loader unmounts
          await new Promise((r) => setTimeout(r, 800));
          isCampaignGenerationLoading.value = false;
          onComplete?.(campaigns);
          return;
        }

        // no task row, but campaigns already exist — generation happened outside of a task we can
        // see, so there is nothing left to wait for
        if (taskState === 'none' && hasCampaigns) {
          isCampaignGenerationLoading.value = false;
          onComplete?.(campaigns);
          return;
        }

        // pending, or queued but not yet picked up — keep waiting
        if (attempts < maxAttempts) {
          campaignPollTimer = setTimeout(poll, intervalMs);
          return;
        }

        isCampaignGenerationLoading.value = false;
        onError?.(new Error('Campaign generation polling timed out'));
      } catch (error) {
        isCampaignGenerationLoading.value = false;
        onError?.(error);
      }
    };

    // a resuming caller has already read the status once, so it can skip straight to the interval
    if (startDelayMs > 0) {
      campaignPollTimer = setTimeout(poll, startDelayMs);
      return;
    }

    await poll();
  }

  /**
   * Show the generation loader and take over polling when a draft is *already* generating its
   * self-service campaigns.
   *
   * Generation state only lives in the tab that submitted the products step. Without this, a
   * second tab — or a colleague on their own laptop — resuming the same draft drops straight into
   * the self-service step and renders its "no campaigns, contact support" empty state, even though
   * nothing is wrong with the order.
   *
   * Resolves to true when generation is still pending and the loader took over.
   */
  async function resumeCampaignGenerationIfPending(
    orderId: string | number,
    options?: { onSettled?: () => void }
  ): Promise<boolean> {
    // don't raise the campaign loader for the status probe — the page-level isLoading already
    // covers the self-service step's empty state during initializeExistingOrder. Raising here
    // would flash the campaign loader on and off for one round-trip when nothing is pending.
    try {
      const res = await fetchOrderGenerateCampaignStatus(orderId);
      const task: Record<string, any> | null = res.task ?? null;

      campaignGenerationTask.value = task;
      generatedCampaigns.value = res.data || [];

      if (resolveCampaignTaskState(task) !== 'pending') return false;

      // pending — raise the loader and poll until it settles
      isCampaignGenerationLoading.value = true;
      pollCampaignGenerationStatus(orderId, {
        startDelayMs: CAMPAIGN_POLL_INTERVAL_MS,
        onComplete: () => options?.onSettled?.(),
        onError: (error) => {
          console.error('Campaign generation did not finish while resuming the draft:', error);
          options?.onSettled?.();
        },
      });

      return true;
    } catch (error) {
      console.error('Failed to read the campaign generation status while resuming the draft:', error);
      return false;
    }
  }

  function stopCampaignGenerationPolling() {
    if (campaignPollTimer) {
      clearTimeout(campaignPollTimer);
      campaignPollTimer = null;
    }
  }

  return {
    order,
    isLoading,
    getOrderById,
    orderImages,
    companyImages,
    companylogo,
    setDefaultCompanyLogo,
    customContentGeneration,
    generateDraft,
    updateDraft,
    hydrateCompanyHierarchy,
    orderDetailsStep,
    // Publication
    publication,
    publicationTemplates,
    shouldCreatePublication,
    isPublicationLoading,
    getPublicationByKey,
    createPublication,
    updatePublication,
    fetchPublicationTemplates,
    fetchCompanyImagesForCurrentOrder,
    isTemplateLoading,
    configurePublicationSteps,
    publicationSavingState,
    // Campaign generation
    isCampaignGenerationLoading,
    campaignGenerationTask,
    campaignGenerationPhase,
    generatedCampaigns,
    pollCampaignGenerationStatus,
    resumeCampaignGenerationIfPending,
    stopCampaignGenerationPolling,
    // Products
    getOrderProducts,
    orderProducts,
    productsToSave,
    selectedProducts,
    expectedCampaignProviders,
    resetState,
    // Import Analytics
    previewImageLoaded,
    previewImageUrl,
    didScanJobPosting,
    scanningInProgress,
    scanTimeline,
    scanTimelineIsDone,
    statusMessages,
    statusMessagesIsDone,
    // Wizard navigation
    currentStepSequence,
    openCart,
    availableSteps,
    visibleSteps,
    currentProgress,
    currentStep,
    canClickNext,
    resolveRequestedStep,
    restoreStepFromQuery,
    onProductsValidated,
    skipUrlStep,
    skipImportStep,
    skipDetailsStep,
    skipMediaStep,
    configureWizardSteps,
    isOrderPlaced,
    markOrderPlaced,
    //Funnel options
    isFunnel,
    generateQuestions,
    optimizeContent,
    stepErrors,
  };
});
